import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard, Car, Calendar, Mail, LogOut, Plus, Edit, Trash2, Eye,
  TrendingUp, CheckCircle, Clock, Upload, Loader2, Settings as SettingsIcon
} from "lucide-react";
import { adminApi, PHP, LOGO_URL } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "bookings", label: "Bookings", icon: Calendar },
  { id: "vehicles", label: "Fleet", icon: Car },
  { id: "messages", label: "Messages", icon: Mail },
  { id: "settings", label: "Settings", icon: SettingsIcon },
];

const BOOKING_STATUSES = [
  { value: "pending", label: "Pending" },
  { value: "pending_payment", label: "Pending Payment" },
  { value: "paid_reserved", label: "Paid / Reserved" },
  { value: "confirmed", label: "Confirmed" },
  { value: "picked_up", label: "Picked Up" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
  { value: "refunded", label: "Refunded" },
];

const emptyVehicle = {
  name: "", brand: "", model: "", year: 2025, type: "car", category: "sedan",
  transmission: "automatic", seats: 5, daily_rate: 0, promo_rate: "", promo_label: "", image: "", description: "",
  features: [], inclusions: [], available: true, drivetrain: ""
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [messages, setMessages] = useState([]);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [vehicleForm, setVehicleForm] = useState(emptyVehicle);
  const [vehicleModalOpen, setVehicleModalOpen] = useState(false);
  const [viewBooking, setViewBooking] = useState(null);

  useEffect(() => {
    if (!localStorage.getItem("pblux_admin_token")) {
      navigate("/admin");
    }
  }, [navigate]);

  const api = adminApi();

  const loadAll = async () => {
    try {
      const [s, b, v, m] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/bookings"),
        api.get("/vehicles"),
        api.get("/admin/contact"),
      ]);
      setStats(s.data); setBookings(b.data); setVehicles(v.data); setMessages(m.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("pblux_admin_token");
        navigate("/admin");
      }
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAll(); }, []);

  const logout = () => {
    localStorage.removeItem("pblux_admin_token");
    localStorage.removeItem("pblux_admin_user");
    navigate("/admin");
  };

  const updateBookingStatus = async (id, payload) => {
    try {
      await api.put(`/admin/bookings/${id}`, payload);
      toast.success("Booking updated");
      loadAll();
    } catch (e) { toast.error("Update failed"); }
  };

  const openVehicleEditor = (v = null) => {
    setEditingVehicle(v);
    setVehicleForm(v ? {
      ...v,
      features: (v.features || []).join(", "),
      inclusions: (v.inclusions || []).join(", "),
      drivetrain: v.drivetrain || ""
    } : { ...emptyVehicle, features: "", inclusions: "" });
    setVehicleModalOpen(true);
  };

  const saveVehicle = async () => {
    if (!vehicleForm.name || !vehicleForm.brand || !vehicleForm.model || !vehicleForm.image) {
      toast.error("Please fill in name, brand, model, and image URL");
      return;
    }
    try {
      const payload = {
        name: vehicleForm.name,
        brand: vehicleForm.brand,
        model: vehicleForm.model,
        year: vehicleForm.year ? parseInt(vehicleForm.year) : null,
        type: vehicleForm.type,
        category: vehicleForm.category,
        transmission: vehicleForm.transmission,
        seats: vehicleForm.seats ? parseInt(vehicleForm.seats) : null,
        daily_rate: parseFloat(vehicleForm.daily_rate) || 0,
        promo_rate: vehicleForm.promo_rate ? parseFloat(vehicleForm.promo_rate) : null,
        promo_label: vehicleForm.promo_label || null,
        image: vehicleForm.image,
        description: vehicleForm.description || "",
        features: typeof vehicleForm.features === "string"
          ? vehicleForm.features.split(",").map(s => s.trim()).filter(Boolean)
          : (vehicleForm.features || []),
        inclusions: typeof vehicleForm.inclusions === "string"
          ? vehicleForm.inclusions.split(",").map(s => s.trim()).filter(Boolean)
          : (vehicleForm.inclusions || []),
        available: vehicleForm.available !== false,
        drivetrain: vehicleForm.drivetrain || null,
      };
      if (editingVehicle) {
        await api.put(`/admin/vehicles/${editingVehicle.id}`, payload);
        toast.success("Vehicle updated");
      } else {
        await api.post("/admin/vehicles", payload);
        toast.success("Vehicle added");
      }
      setVehicleModalOpen(false);
      setEditingVehicle(null);
      setVehicleForm(emptyVehicle);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const deleteVehicle = async (id) => {
    if (!window.confirm("Delete this vehicle?")) return;
    try {
      await api.delete(`/admin/vehicles/${id}`);
      toast.success("Vehicle deleted");
      loadAll();
    } catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div className="min-h-screen bg-ink-900 flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="w-64 border-r border-gold-500/15 glass-dark min-h-screen p-6 hidden md:flex md:flex-col">
        <Link to="/" className="flex items-center gap-3">
          <img src={LOGO_URL} alt="PB-Lux" className="h-10 w-10 rounded-full border border-gold-500/30" />
          <div>
            <p className="font-display text-xl text-gold-200 leading-none">PB-LUX</p>
            <p className="text-[9px] tracking-[0.3em] text-gold-500/80 uppercase mt-1">Admin</p>
          </div>
        </Link>
        <nav className="mt-10 flex-1 space-y-1">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition ${tab === t.id ? "bg-gold-500/15 text-gold-200 border border-gold-500/30" : "text-foreground/65 hover:text-gold-200 hover:bg-gold-500/5"}`}
            >
              <t.icon className="w-4 h-4" />
              {t.label}
            </button>
          ))}
        </nav>
        <button onClick={logout} className="flex items-center gap-3 px-4 py-3 text-sm text-foreground/55 hover:text-red-400 transition" data-testid="admin-logout">
          <LogOut className="w-4 h-4" /> Sign Out
        </button>
      </aside>

      {/* Main */}
      <main className="flex-1 p-6 md:p-10">
        {/* Mobile tabs */}
        <div className="md:hidden flex gap-2 overflow-x-auto pb-3 mb-4">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`flex-shrink-0 px-4 py-2 rounded-full text-xs ${tab === t.id ? "bg-gold-500/20 text-gold-200 border border-gold-500/40" : "text-foreground/65 border border-gold-500/20"}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Overview */}
        {tab === "overview" && stats && (
          <div data-testid="admin-overview">
            <h1 className="font-display text-4xl">Welcome back.</h1>
            <p className="text-foreground/55 mt-1">Here's what's happening at PB-Lux.</p>

            <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-5">
              <StatCard icon={TrendingUp} label="Total Revenue" value={PHP(stats.total_revenue)} />
              <StatCard icon={Calendar} label="Total Bookings" value={stats.total_bookings} />
              <StatCard icon={Clock} label="Pending" value={stats.pending_bookings} accent="yellow" />
              <StatCard icon={CheckCircle} label="Confirmed" value={stats.confirmed_bookings} accent="green" />
            </div>

            <div className="mt-10">
              <h2 className="font-display text-2xl mb-4">Recent Bookings</h2>
              <div className="glass-card rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-ink-700/50">
                      <tr className="text-left text-[10px] uppercase tracking-widest text-foreground/55">
                        <th className="px-5 py-3">Ref</th>
                        <th className="px-5 py-3">Customer</th>
                        <th className="px-5 py-3">Vehicle</th>
                        <th className="px-5 py-3">Total</th>
                        <th className="px-5 py-3">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.slice(0, 5).map((b) => (
                        <tr key={b.id} className="border-t border-gold-500/10 hover:bg-gold-500/5">
                          <td className="px-5 py-4 font-mono text-xs text-gold-300">{b.booking_ref}</td>
                          <td className="px-5 py-4">{b.customer_name}</td>
                          <td className="px-5 py-4 text-foreground/75">{b.vehicle_name}</td>
                          <td className="px-5 py-4 text-gold-300">{PHP(b.total)}</td>
                          <td className="px-5 py-4 capitalize"><StatusPill s={b.booking_status} /></td>
                        </tr>
                      ))}
                      {bookings.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-foreground/40">No bookings yet</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bookings */}
        {tab === "bookings" && (
          <div data-testid="admin-bookings">
            <h1 className="font-display text-4xl">Bookings</h1>
            <div className="mt-8 glass-card rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-ink-700/50">
                    <tr className="text-left text-[10px] uppercase tracking-widest text-foreground/55">
                      <th className="px-5 py-3">Ref</th>
                      <th className="px-5 py-3">Customer</th>
                      <th className="px-5 py-3">Vehicle</th>
                      <th className="px-5 py-3">Dates</th>
                      <th className="px-5 py-3">Total</th>
                      <th className="px-5 py-3">Payment</th>
                      <th className="px-5 py-3">Status</th>
                      <th className="px-5 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.map((b) => (
                      <tr key={b.id} className="border-t border-gold-500/10 hover:bg-gold-500/5" data-testid={`admin-booking-${b.id}`}>
                        <td className="px-5 py-4 font-mono text-xs text-gold-300">{b.booking_ref}</td>
                        <td className="px-5 py-4">
                          <p>{b.customer_name}</p>
                          <p className="text-[11px] text-foreground/50">{b.customer_email}</p>
                        </td>
                        <td className="px-5 py-4 text-foreground/75">{b.vehicle_name}</td>
                        <td className="px-5 py-4 text-xs">
                          {new Date(b.pickup_date).toLocaleDateString()}<br/>
                          <span className="text-foreground/50">→ {new Date(b.return_date).toLocaleDateString()}</span>
                        </td>
                        <td className="px-5 py-4 text-gold-300">{PHP(b.total)}</td>
                        <td className="px-5 py-4 text-xs capitalize">{b.payment_method.replace("_"," ")}</td>
                        <td className="px-5 py-4"><StatusPill s={b.booking_status} /></td>
                        <td className="px-5 py-4">
                          <div className="flex gap-1">
                            <button onClick={() => setViewBooking(b)} className="p-2 hover:bg-gold-500/10 rounded text-gold-300" data-testid={`view-booking-${b.id}`}>
                              <Eye className="w-4 h-4" />
                            </button>
                            <Select value={b.booking_status} onValueChange={(v) => updateBookingStatus(b.id, { booking_status: v })}>
                              <SelectTrigger className="w-[140px] h-8 text-xs bg-ink-800 border-gold-500/20" data-testid={`status-select-${b.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {BOOKING_STATUSES.map((s) => (
                                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {bookings.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-foreground/40">No bookings yet</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Vehicles */}
        {tab === "vehicles" && (
          <div data-testid="admin-vehicles">
            <div className="flex items-center justify-between">
              <h1 className="font-display text-4xl">Fleet Management</h1>
              <button onClick={() => openVehicleEditor()} className="btn-gold !py-2 !px-5" data-testid="add-vehicle-btn">
                <Plus className="w-4 h-4" /> Add Vehicle
              </button>
            </div>
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {vehicles.map((v) => (
                <div key={v.id} className="glass-card rounded-xl overflow-hidden" data-testid={`admin-vehicle-${v.id}`}>
                  <img src={v.image} alt={v.name} className="w-full h-40 object-cover" />
                  <div className="p-4">
                    <p className="text-[10px] uppercase tracking-widest text-gold-500/80">{v.brand}</p>
                    <p className="font-display text-xl mt-1">{v.model}</p>
                    <p className="text-xs text-foreground/55">{v.name}</p>
                    <p className="text-gold-300 mt-2 font-medium">{PHP(v.daily_rate)}/day</p>
                    <div className="mt-3 flex gap-2">
                      <button onClick={() => openVehicleEditor(v)} className="flex-1 btn-outline-gold !py-2 !text-[11px] !px-3" data-testid={`edit-vehicle-${v.id}`}>
                        <Edit className="w-3 h-3" /> Edit
                      </button>
                      <button onClick={() => deleteVehicle(v.id)} className="px-3 py-2 rounded-full border border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs" data-testid={`delete-vehicle-${v.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Messages */}
        {tab === "messages" && (
          <div data-testid="admin-messages">
            <h1 className="font-display text-4xl">Contact Messages</h1>
            <div className="mt-8 space-y-4">
              {messages.map((m) => (
                <div key={m.id} className="glass-card rounded-2xl p-6">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="font-display text-xl">{m.name}</p>
                      <p className="text-xs text-gold-300 mt-1">{m.email} {m.phone && `· ${m.phone}`}</p>
                      {m.subject && <p className="text-sm text-foreground/65 mt-2">Subject: {m.subject}</p>}
                    </div>
                    <p className="text-[11px] text-foreground/40">{new Date(m.created_at).toLocaleString()}</p>
                  </div>
                  <p className="text-sm text-foreground/80 mt-4 leading-relaxed">{m.message}</p>
                </div>
              ))}
              {messages.length === 0 && <p className="text-center text-foreground/40 py-16">No messages yet</p>}
            </div>
          </div>
        )}
        {/* Settings */}
        {tab === "settings" && <SettingsTab api={api} />}
      </main>

      {/* Booking detail modal */}
      <Dialog open={!!viewBooking} onOpenChange={(o) => !o && setViewBooking(null)}>
        <DialogContent className="bg-ink-800 border-gold-500/30 max-w-2xl max-h-[90vh] overflow-y-auto">
          {viewBooking && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-3xl text-gold-200">Booking {viewBooking.booking_ref}</DialogTitle>
              </DialogHeader>
              <div className="grid sm:grid-cols-2 gap-5 text-sm mt-4">
                <Field l="Customer" v={viewBooking.customer_name} />
                <Field l="Email" v={viewBooking.customer_email} />
                <Field l="Phone" v={viewBooking.customer_phone} />
                <Field l="Vehicle" v={viewBooking.vehicle_name} />
                <Field l="Pickup" v={new Date(viewBooking.pickup_date).toLocaleDateString()} />
                <Field l="Return" v={new Date(viewBooking.return_date).toLocaleDateString()} />
                <Field l="Days" v={viewBooking.days} />
                <Field l="Country" v={viewBooking.country || "—"} />
                <Field l="Pickup Location" v={viewBooking.pickup_location} />
                <Field l="Drop-off" v={viewBooking.dropoff_address || (viewBooking.dropoff_location_type === "el_nido" ? "El Nido" : viewBooking.dropoff_location_type === "ppc_hotel" ? "Hotel Collection (PPC)" : "PB-Lux Shop")} />
                <Field l="Daily Rate" v={PHP(viewBooking.daily_rate)} />
                <Field l="Subtotal" v={PHP(viewBooking.subtotal)} />
                <Field l="Pickup/Delivery Fee" v={PHP(viewBooking.pickup_fee ?? viewBooking.delivery_fee)} />
                <Field l="Drop-off Fee" v={PHP(viewBooking.dropoff_fee || 0)} />
                <Field l="Carwash" v={PHP(viewBooking.carwash_fee)} />
                <Field l="Reservation Fee" v={PHP(viewBooking.reservation_fee || viewBooking.daily_rate)} />
                <Field l="Remaining Balance" v={PHP(viewBooking.remaining_balance ?? Math.max(0, (viewBooking.total || 0) - (viewBooking.reservation_fee || 0)))} />
                <Field l="Total" v={PHP(viewBooking.total)} highlight />
                <Field l="Payment" v={viewBooking.payment_method.replace("_"," ")} />
                <Field l="Payment Scope" v={viewBooking.payment_scope === "full" ? "Pay in Full" : "Reservation fee"} />
                <Field l="Amount Due Now" v={PHP(viewBooking.amount_due_now || viewBooking.reservation_fee)} />
                <Field l="Amount Paid" v={PHP(viewBooking.amount_paid || 0)} />
                <Field l="Security Option" v={viewBooking.collateral_option === "deposit" ? `Deposit ${PHP(viewBooking.security_deposit)}` : "Passport (verify on pickup)"} />
                {(viewBooking.addons || []).length > 0 && <Field l="Add-ons" v={`${viewBooking.addons.join(", ")} (${PHP(viewBooking.addons_fee || 0)})`} />}
                <Field l="Booking Status" v={viewBooking.booking_status} />
                <Field l="Payment Status" v={viewBooking.payment_status} />
              </div>
              {viewBooking.notes && <div className="mt-4"><Field l="Notes" v={viewBooking.notes} /></div>}
              <div className="mt-6 flex justify-end">
                <Select value={viewBooking.payment_status} onValueChange={(v) => { updateBookingStatus(viewBooking.id, { payment_status: v }); setViewBooking({ ...viewBooking, payment_status: v }); }}>
                  <SelectTrigger className="w-[200px] bg-ink-700 border-gold-500/30">
                    <SelectValue placeholder="Payment status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Mark Pending</SelectItem>
                    <SelectItem value="pending_verification">Mark Pending Verification</SelectItem>
                    <SelectItem value="partially_paid">Mark Partially Paid (res. fee)</SelectItem>
                    <SelectItem value="fully_paid">Mark Fully Paid</SelectItem>
                    <SelectItem value="refunded">Mark Refunded</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Vehicle editor modal */}
      <Dialog open={vehicleModalOpen} onOpenChange={(o) => { if (!o) { setVehicleModalOpen(false); setEditingVehicle(null); }}}>
        <DialogContent className="bg-ink-800 border-gold-500/30 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl text-gold-200">
              {editingVehicle ? "Edit Vehicle" : "Add New Vehicle"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            <FormField label="Display Name *">
              <Input value={vehicleForm.name} onChange={(e) => setVehicleForm({ ...vehicleForm, name: e.target.value })} placeholder="Toyota Vios 2025 (AT)" data-testid="vehicle-form-name" />
            </FormField>
            <FormField label="Brand *">
              <Input value={vehicleForm.brand} onChange={(e) => setVehicleForm({ ...vehicleForm, brand: e.target.value })} placeholder="Toyota" />
            </FormField>
            <FormField label="Model *">
              <Input value={vehicleForm.model} onChange={(e) => setVehicleForm({ ...vehicleForm, model: e.target.value })} placeholder="Vios" />
            </FormField>
            <FormField label="Year">
              <Input type="number" value={vehicleForm.year || ""} onChange={(e) => setVehicleForm({ ...vehicleForm, year: e.target.value })} />
            </FormField>
            <FormField label="Type">
              <Select value={vehicleForm.type} onValueChange={(v) => setVehicleForm({ ...vehicleForm, type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="car">Car</SelectItem>
                  <SelectItem value="motorbike">Motorbike</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Category">
              <Select value={vehicleForm.category} onValueChange={(v) => setVehicleForm({ ...vehicleForm, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="hatchback">Hatchback</SelectItem>
                  <SelectItem value="sedan">Sedan</SelectItem>
                  <SelectItem value="mpv">MPV</SelectItem>
                  <SelectItem value="suv">SUV</SelectItem>
                  <SelectItem value="pickup">Pickup</SelectItem>
                  <SelectItem value="van">Van</SelectItem>
                  <SelectItem value="scooter">Scooter</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Transmission">
              <Select value={vehicleForm.transmission} onValueChange={(v) => setVehicleForm({ ...vehicleForm, transmission: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="automatic">Automatic</SelectItem>
                  <SelectItem value="manual">Manual</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Seats">
              <Input type="number" value={vehicleForm.seats || ""} onChange={(e) => setVehicleForm({ ...vehicleForm, seats: e.target.value })} />
            </FormField>
            <FormField label="Daily Rate (₱) *">
              <Input type="number" value={vehicleForm.daily_rate} onChange={(e) => setVehicleForm({ ...vehicleForm, daily_rate: e.target.value })} />
            </FormField>
            <FormField label="Promo Rate (₱, optional)">
              <Input type="number" value={vehicleForm.promo_rate || ""} onChange={(e) => setVehicleForm({ ...vehicleForm, promo_rate: e.target.value })} placeholder="e.g. 400 (must be below daily rate)" data-testid="vehicle-form-promo-rate" />
            </FormField>
            <FormField label="Promo Label (optional)">
              <Input value={vehicleForm.promo_label || ""} onChange={(e) => setVehicleForm({ ...vehicleForm, promo_label: e.target.value })} placeholder="Facebook Special" data-testid="vehicle-form-promo-label" />
            </FormField>
            <FormField label="Drivetrain (e.g. 4x4)">
              <Input value={vehicleForm.drivetrain || ""} onChange={(e) => setVehicleForm({ ...vehicleForm, drivetrain: e.target.value })} />
            </FormField>
            <FormField label="Vehicle Photo *" full>
              <ImageUploader
                value={vehicleForm.image}
                onChange={(url) => setVehicleForm({ ...vehicleForm, image: url })}
              />
            </FormField>
            <FormField label="Description" full>
              <Textarea value={vehicleForm.description} onChange={(e) => setVehicleForm({ ...vehicleForm, description: e.target.value })} rows={3} />
            </FormField>
            <FormField label="Features (comma separated)" full>
              <Input value={vehicleForm.features || ""} onChange={(e) => setVehicleForm({ ...vehicleForm, features: e.target.value })} placeholder="A/C, Bluetooth, Reverse Camera" />
            </FormField>
            <FormField label="Inclusions (comma separated)" full>
              <Input value={vehicleForm.inclusions || ""} onChange={(e) => setVehicleForm({ ...vehicleForm, inclusions: e.target.value })} placeholder="1L starter fuel, 1 helmet" />
            </FormField>
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button onClick={() => { setVehicleModalOpen(false); setEditingVehicle(null); }} className="btn-outline-gold !py-2 !px-5">Cancel</button>
            <button onClick={saveVehicle} className="btn-gold !py-2 !px-5" data-testid="vehicle-form-save">
              {editingVehicle ? "Save Changes" : "Add Vehicle"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FormField({ label, children, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <Label className="text-xs uppercase tracking-wider">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function SettingsTab({ api }) {
  const [policy, setPolicy] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/settings").then((r) => setPolicy(r.data.cancellation_policy || "")).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const save = async () => {
    try {
      setSaving(true);
      await api.put("/admin/settings", { cancellation_policy: policy });
      toast.success("Settings saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div data-testid="admin-settings">
      <h1 className="font-display text-4xl">Settings</h1>
      <div className="mt-8 glass-card rounded-2xl p-6 max-w-2xl">
        <p className="kicker">Cancellation / Refund Policy</p>
        <p className="text-xs text-foreground/55 mt-2">Shown to customers at checkout before they pay. Keep it consistent with your actual PB-Lux policy.</p>
        <Textarea value={policy} onChange={(e) => setPolicy(e.target.value)} rows={6} className="mt-4 bg-ink-800/60 border-gold-500/20" data-testid="settings-policy-input" />
        <button onClick={save} disabled={saving} className="btn-gold !py-2 !px-6 mt-5 disabled:opacity-60" data-testid="settings-save">
          {saving ? "Saving…" : "Save Policy"}
        </button>
      </div>
    </div>
  );
}

function ImageUploader({ value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ["image/jpeg", "image/png", "image/webp", "image/gif", "image/heic", "image/heif"];
    if (!allowed.includes(file.type)) {
      toast.error("Unsupported image type. Use JPG, PNG, or WebP.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image too large (max 10 MB).");
      return;
    }
    try {
      setUploading(true);
      const fd = new FormData();
      fd.append("file", file);
      const r = await adminApi().post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(r.data.url);
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="space-y-3">
      {value && (
        <div className="relative">
          <img src={value} alt="Vehicle preview" className="w-full max-h-56 object-cover rounded-lg border border-gold-500/20" />
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute top-2 right-2 px-2 py-1 rounded-md bg-ink-900/80 text-xs text-red-300 hover:bg-red-500/20 border border-red-500/30"
            data-testid="remove-image-btn"
          >
            Remove
          </button>
        </div>
      )}
      <div className="flex flex-col sm:flex-row gap-2">
        <label
          className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg cursor-pointer border-2 border-dashed transition ${uploading ? "border-gold-500/30 bg-gold-500/5" : "border-gold-500/40 hover:border-gold-500 hover:bg-gold-500/10"}`}
          data-testid="upload-image-label"
        >
          {uploading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin text-gold-400" />
              <span className="text-sm text-gold-300">Uploading…</span>
            </>
          ) : (
            <>
              <Upload className="w-4 h-4 text-gold-400" />
              <span className="text-sm text-gold-200">{value ? "Replace photo" : "Upload from device"}</span>
            </>
          )}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif"
            className="hidden"
            onChange={handleFile}
            disabled={uploading}
            data-testid="upload-image-input"
          />
        </label>
      </div>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="…or paste a public image URL (https://…)"
        data-testid="vehicle-form-image"
        className="text-xs"
      />
      <p className="text-[11px] text-foreground/50">Tip: Take photos in landscape. JPG/PNG/WebP up to 10 MB.</p>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {
  const colors = { yellow: "text-yellow-300", green: "text-green-300", red: "text-red-300" };
  return (
    <div className="glass-card rounded-2xl p-5">
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-widest text-foreground/55">{label}</p>
        <Icon className={`w-4 h-4 ${colors[accent] || "text-gold-400"}`} />
      </div>
      <p className="font-display text-3xl mt-3 text-gold-200">{value}</p>
    </div>
  );
}

function StatusPill({ s }) {
  const map = {
    pending: "bg-yellow-500/15 text-yellow-300 border-yellow-500/40",
    pending_payment: "bg-orange-500/15 text-orange-300 border-orange-500/40",
    paid_reserved: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
    confirmed: "bg-green-500/15 text-green-300 border-green-500/40",
    picked_up: "bg-cyan-500/15 text-cyan-300 border-cyan-500/40",
    completed: "bg-blue-500/15 text-blue-300 border-blue-500/40",
    cancelled: "bg-red-500/15 text-red-300 border-red-500/40",
    paid: "bg-green-500/15 text-green-300 border-green-500/40",
    partially_paid: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
    fully_paid: "bg-green-500/15 text-green-300 border-green-500/40",
    pending_verification: "bg-amber-500/15 text-amber-300 border-amber-500/40",
    payment_failed: "bg-red-500/15 text-red-300 border-red-500/40",
    refunded: "bg-red-500/15 text-red-300 border-red-500/40",
  };
  return <span className={`text-[10px] uppercase tracking-widest px-2.5 py-0.5 rounded-full border ${map[s] || "bg-gold-500/15 text-gold-300 border-gold-500/40"}`}>{(s || "").replace(/_/g, " ")}</span>;
}

function Field({ l, v, highlight }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest text-foreground/50">{l}</p>
      <p className={`mt-1 ${highlight ? "font-display text-2xl text-gold-300" : "text-foreground"} capitalize`}>{v}</p>
    </div>
  );
}
